import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import defaultIcon from "../../../images/emojiPicker.svg";
import calculateFloatingPosition from "../../../utils/floatingPosition";
import "./index.scss";

const DEFAULT_PICKER_Z_INDEX = 7000;

export default function EmojiPicker({ onSelectEmoji, iconSrc = defaultIcon, iconAlt = "Add reaction", zIndex = DEFAULT_PICKER_Z_INDEX, overlayTrigger = false, onOpenChange = () => {}, anchorSelector = null, children }) {
    const [isOpen, setIsOpen] = useState(false);
    const pickerRef = useRef(null);
    const panelRef = useRef(null);
    const triggerRef = useRef(null);
    const [panelCoords, setPanelCoords] = useState({ top: 0, left: 0 });
    const [verticalPlacement, setVerticalPlacement] = useState("above");

    const handleToggle = () => setIsOpen((prev) => !prev);

    // Called when user picks an emoji
    const handleSelect = (emoji) => {
        onSelectEmoji(emoji);
        // Do NOT close the picker here
    };

    // Close picker if click outside
    useEffect(() => {
        if (!isOpen) return;
        function handleClickOutside(event) {
            const clickInsideTrigger = pickerRef.current?.contains(event.target);
            const clickInsidePanel = panelRef.current?.contains(event.target);
            if (!clickInsideTrigger && !clickInsidePanel) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    // Notify parent when open state changes (e.g., to keep host UI mounted).
    // Only on actual transitions: notifying `false` on mount makes hosts that
    // mount the picker lazily (message actions panel) dismiss themselves.
    const lastNotifiedOpenRef = useRef(false);
    useEffect(() => {
        const nextOpen = Boolean(isOpen);
        if (lastNotifiedOpenRef.current === nextOpen) return;
        lastNotifiedOpenRef.current = nextOpen;
        try { onOpenChange(nextOpen); } catch (_) { /* no-op */ }
    }, [isOpen, onOpenChange]);

    const updateFloatingCoords = useCallback(() => {
        const panel = panelRef.current;
        const trigger = triggerRef.current;
        if (!panel || !trigger) return;

        // Optionally anchor to a surrounding element (e.g. the whole composer)
        // instead of the trigger button itself.
        const anchorEl = (anchorSelector && trigger.closest(anchorSelector)) || trigger;
        const triggerRect = anchorEl.getBoundingClientRect();

        const anchor = {
            top: triggerRect.top,
            left: triggerRect.left,
            width: triggerRect.width,
            height: triggerRect.height,
        };

        const nextCoords = calculateFloatingPosition({
            container: document.documentElement,
            dropdown: panel,
            anchor,
            gap: 8,
            offset: { x: 0, y: 8 },
            align: "start",
            flip: true,
            strategy: "fixed",
        });

        const panelHeight = panel.getBoundingClientRect().height || panel.offsetHeight || 0;
        const placedAbove = nextCoords.top + panelHeight <= triggerRect.top + 1;

        setPanelCoords(nextCoords);
        setVerticalPlacement(placedAbove ? "above" : "below");
    }, [anchorSelector]);

    // Position picker so it stays in viewport and flips above/below when needed.
    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        updateFloatingCoords();
        const raf = requestAnimationFrame(updateFloatingCoords);

        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
    }, [isOpen, updateFloatingCoords]);

    // Close on scroll/resize to mimic other floating elements behavior
    useEffect(() => {
        if (!isOpen) return undefined;
        const close = () => setIsOpen(false);
        const handleScroll = (event) => {
            const scrollTarget = event?.target;
            if (
                panelRef.current
                && scrollTarget
                && typeof Node !== "undefined"
                && scrollTarget instanceof Node
                && panelRef.current.contains(scrollTarget)
            ) {
                return;
            }
            close();
        };

        document.addEventListener("scroll", handleScroll, true);
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", close);
        return () => {
            document.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", close);
        };
    }, [isOpen]);

    // emoji-mart renders into Shadow DOM; apply runtime style overrides after mount.
    useEffect(() => {
        if (!isOpen) return undefined;

        let rafId = 0;
        const applyShadowOverrides = () => {
            const shadowRoot = panelRef.current?.querySelector("em-emoji-picker")?.shadowRoot;
            const root = shadowRoot?.querySelector("#root");

            if (!root) {
                rafId = requestAnimationFrame(applyShadowOverrides);
                return;
            }

            root.style.backgroundColor = "transparent";

            const styleId = "muzone-emoji-picker-overrides";
            if (!shadowRoot.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.textContent = [
                    // Custom properties inherit through the shadow boundary, so
                    // these read the theme's slots off the host element rather
                    // than pinning a palette the theme cannot reach.
                    "#nav.padding { border-top-left-radius: 16px !important; border-top-right-radius: 16px !important; background: var(--emoji-picker-bar-bg); }",
                    ".sticky.padding-small.align-l { border-radius: 16px; background: var(--emoji-picker-bar-bg); }",
                    ".search input[type=\"search\"]:focus { box-shadow: inset 0 0 0 1px var(--emoji-picker-focus-ring), 0 1px 3px rgba(65, 69, 73, 0.2); }",
                    // emoji-mart paints the placeholder as `color: inherit` at
                    // `opacity: .6`, a ratio picked for its own near-white ramp.
                    // On a light field that lands at 2.49:1, so it takes the
                    // theme's placeholder ink at full opacity instead.
                    ".search input[type=\"search\"]::placeholder { color: var(--emoji-picker-placeholder-ink); opacity: 1; }",
                ].join("\n");
                shadowRoot.appendChild(style);
            }
        };

        applyShadowOverrides();

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isOpen]);

    // When used as an overlayTrigger (embedded inside another visible button),
    // restrict the invisible trigger to the button's content box instead of
    // stretching to its container, so it cannot cover adjacent controls.
    const wrapperStyle = overlayTrigger ? { position: "relative", display: "inline-block" } : { position: "relative" };
    const triggerStyle = overlayTrigger ? { position: "absolute", inset: 0, opacity: 0, padding: 0, width: "100%", height: "100%" } : undefined;
    const canUseDOM = typeof document !== "undefined";
    const pickerZIndex = Number.isFinite(zIndex) ? zIndex : DEFAULT_PICKER_Z_INDEX;

    return (
        <div style={wrapperStyle} ref={pickerRef}>
            {overlayTrigger ? (
                <>
                    <button className={`emoji-btn ${isOpen ? "is-open" : ""}`} type="button" onClick={handleToggle} aria-label={iconAlt} style={triggerStyle} ref={triggerRef} />
                    {children}
                </>
            ) : (
                <button className={"emoji-btn"} type="button" onClick={handleToggle} aria-label={iconAlt} ref={triggerRef}>
                    <span
                        className="icon-mask emoji-icon"
                        aria-hidden="true"
                        style={{ "--icon-mask-url": `url("${iconSrc}")` }}
                    />
                </button>
            )}
            {isOpen && canUseDOM && createPortal(
                <div
                    ref={panelRef}
                    style={{
                        position: "fixed",
                        top: panelCoords.top,
                        left: panelCoords.left,
                        "--emoji-picker-z-index": `${pickerZIndex}`,
                    }}
                    className={`emoji-picker-modal emoji-picker-modal--${verticalPlacement}`}
                >
                    <Picker data={data} onEmojiSelect={handleSelect} theme="light" />
                </div>,
                document.body
            )}
        </div>
    );
}

EmojiPicker.propTypes = {
    onSelectEmoji: PropTypes.func.isRequired,
    iconSrc: PropTypes.string,
    iconAlt: PropTypes.string,
    anchorSelector: PropTypes.string,
    zIndex: PropTypes.number,
    overlayTrigger: PropTypes.bool,
    onOpenChange: PropTypes.func,
    children: PropTypes.node,
};
